import { Col, Row } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import type { EditModel } from 'edit/EditModel';
import { EditDialog } from 'edit/EditDialog';
import { EditFolderReference } from 'edit/reference/EditFolderReference';
import { EditString } from 'components/forms/EditString';
import { EditText } from 'components/forms/EditText';
import { EditBoolean } from 'components/forms/EditBoolean';
import { EditZoneReference } from 'edit/reference/EditZoneReference';
import { DetailCard } from 'components/details/DetailCard';
import { DetailList } from 'components/details/DetailList';
import type { RecurrenceDefinition } from 'api/recurrence_definitions/endpoints/getRecurrenceDefinition';

import type { EditableRecurrenceDefinition } from './EditableRecurrenceDefinition';
import { RecurrenceRuleFields } from './RecurrenceRuleFields';

interface Props {
  readonly model: EditModel<EditableRecurrenceDefinition, RecurrenceDefinition | null>;
  readonly onSaved: () => void;
  readonly onClose: () => void;
}

export const EditRecurrenceDefinitionDialog = ({ model, onSaved, onClose }: Props) => (
  <EditDialog model={model} onSaved={onSaved} onClose={onClose} entityName="Recurrence Definition">
    {(entity) => <FormFields entity={entity} />}
  </EditDialog>
);

interface FieldsProps {
  readonly entity: EditableRecurrenceDefinition;
}

const FormFields = observer<FieldsProps>(({ entity }) => (
  <>
    <Row>
      <Col md={12}>
        <EditString label="Name" value={entity.name} onChange={(v) => (entity.name = v)} mandatory />
      </Col>
    </Row>

    <Row>
      <Col md={6}>
        <EditZoneReference
          value={entity.zone}
          onChange={(zone) => {
            // Folder is zone-scoped — clear it when the zone changes.
            entity.zone = zone;
            entity.folder = null;
          }}
          resourceType="recurrence_definitions"
          action={entity.isCreating ? 'create' : 'update'}
          mandatory
        />
      </Col>
      <Col md={6}>
        <EditFolderReference value={entity.folder} onChange={(v) => (entity.folder = v)} zoneName={entity.zone?.name} />
      </Col>
    </Row>

    <Row>
      <Col md={12}>
        <EditText label="Description" value={entity.description} onChange={(v) => (entity.description = v)} />
      </Col>
    </Row>

    <Row>
      <Col md={12}>
        <EditBoolean
          label="Resubmit on error"
          value={entity.resubmitOnError}
          onChange={(v) => (entity.resubmitOnError = v)}
          helpText="Automatically resubmit the job if it fails"
        />
      </Col>
    </Row>

    <DetailList
      items={entity.recurrences}
      getKey={(recurrence) => recurrence.key}
      onAdd={entity.createRecurrence}
      addLabel="Add recurrence"
      renderItem={(recurrence, index) => (
        <DetailCard
          index={index + 1}
          title={recurrence.description || 'Recurrence'}
          subtitle={recurrence.explanation ?? undefined}
          collapsible
          onRemove={recurrence.delete}
          // Backend requires at least one recurrence — block removing the last.
          removeDisabled={entity.recurrences.length <= 1}
        >
          <RecurrenceRuleFields recurrence={recurrence} />
        </DetailCard>
      )}
    />
  </>
));
